import { PreviewDirectory, PreviewFile } from "collaboration-service";
import { fadeColor, getColor, Icon, lightenColor, styled } from "imaginarity-react-ui";
import * as React from "react";

type ShowPreviewDirectoryProps = {
    directory: PreviewDirectory;
};

const Container = styled.div`
    overflow: auto;
    max-height: 360px;
    border: 1px solid ${p => getColor(p, "@middleLightGrey")};
    padding: 10px 12px;
    margin-bottom: 20px;
    font-size: 0.9rem;
    line-height: 1.5;
    font-family: ${p => p.theme.fontFamily ?? "inherit"};
`;

const Row = styled.div<{ clickable: boolean }>`
    display: flex;
    align-items: center;
    padding: 3px 6px;
    border-radius: 0px;
    cursor: ${p => (p.clickable ? "pointer" : "default")};
    user-select: none;
    color: ${p => getColor(p, "@darkerGrey")};
    transition: background-color 0.1s ease-out;
    &:hover {
        background: ${p => (p.clickable ? fadeColor(p, "@accentLightBlue", 5) : "transparent")};
    }
`;

const Chevron = styled.span<{ open: boolean; visible: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 14px;
    width: 14px;
    margin-right: 4px;
    opacity: ${p => (p.visible ? 1 : 0)};
    transform: rotate(${p => (p.open ? 90 : 0)}deg);
    transition: transform 0.15s ease-out;
    color: ${p => getColor(p, "@darkerGrey")};
`;

const IconWrap = styled.span<{ folder: boolean }>`
    display: inline-flex;
    align-items: center;
    margin-right: 8px;
    color: ${p => (p.folder ? getColor(p, "@accent") : getColor(p, "@darkGrey"))};
`;

const Name = styled.span<{ folder: boolean; open?: boolean }>`
    flex: 1 1 auto;
    font-weight: ${p => (p.folder && p.open ? "bolder" : p.folder ? 400 : 400)};
    color: ${p => (p.folder && p.open ? getColor(p, "@darkGrey") : p.folder ? getColor(p, "@darkGrey") : lightenColor(p, "@darkGrey", 170))};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Count = styled.span`
    flex: 0 0 auto;
    margin-left: 8px;
    color: ${p => getColor(p, "@middleLightGrey")};
    font-size: 0.8em;
    font-variant-numeric: tabular-nums;
`;

const Children = styled.div<{ open: boolean }>`
    display: grid;
    grid-template-rows: ${p => (p.open ? "1fr" : "0fr")};
    opacity: ${p => (p.open ? 1 : 0)};
    transition: grid-template-rows 0.2s ease-out, opacity 0.2s ease-out, border-color 0.2s ease-out;
    margin-left: 13px;
    padding-left: 7px;
    border-left: 1px dashed ${p => (p.open ? getColor(p, "@middleLightGrey") : "transparent")};
`;

const ChildrenInner = styled.div`
    overflow: hidden;
    min-height: 0;
`;

type DirProps = {
    curDir: PreviewDirectory;
    depth?: number;
};

const Dir = ({ curDir, depth = 0 }: DirProps) => {
    const [open, setOpen] = React.useState<boolean>(depth === 0);
    const dirs = curDir.directories ?? [];
    const files = curDir.files ?? [];
    const total = dirs.length + files.length;
    const isEmpty = total === 0;

    return (
        <div>
            <Row clickable={!isEmpty} onClick={isEmpty ? undefined : () => setOpen(!open)}>
                <Chevron open={open} visible={!isEmpty}>
                    <Icon name="chevron right" size="0.7rem" />
                </Chevron>
                <IconWrap folder>
                    <Icon name={open && !isEmpty ? "folder open" : "folder"} size="1rem" />
                </IconWrap>
                <Name folder open={open && !isEmpty}>{curDir.name || "/"}</Name>
                {!isEmpty && <Count>{total}</Count>}
            </Row>
            <Children open={open}>
                <ChildrenInner>
                    {dirs.map((c, i) => (
                        <Dir key={`d-${c.name ?? ""}-${i}`} curDir={c} depth={depth + 1} />
                    ))}
                    {files.map((f, i) => (
                        <FileRow key={`f-${f.name ?? ""}-${i}`} file={f} />
                    ))}
                </ChildrenInner>
            </Children>
        </div>
    );
};

const FileRow = ({ file }: { file: PreviewFile }) => (
    <Row clickable={false}>
        <Chevron open={false} visible={false}>
            <Icon name="chevron right" size="0.7rem" />
        </Chevron>
        <IconWrap folder={false}>
            <Icon name="file" size="1rem" />
        </IconWrap>
        <Name folder={false}>{file.name}</Name>
    </Row>
);

const ShowPreviewDirectory = (p: ShowPreviewDirectoryProps) => (
    <Container>
        <Dir curDir={p.directory} />
    </Container>
);

export default ShowPreviewDirectory;
